<blaze-table-popup-menu #host class="column-editor-panel bd-menu" (closePanel)="close()">
  <ng-container title>{{ title }}</ng-container>

  <ng-container content>
    <!-- #region General field properties (name, field type) -->
    <div class="field-properties">
      <div class="field-name">Name</div>
      <input
        #nameField
        [(ngModel)]="fieldName"
        (change)="processColumnName()"
        [disabled]="!!fieldType?.isProfilePicture"
        name="fieldName"
        class="field-name-input"
        autocomplete="off"
      />
    </div>
    <!-- #endregion -->

    <!-- #region Field type select -->
    <div class="field-properties mt-8">
      <div class="field-name">Field type</div>

      <div class="field-type-picker" (click)="fieldTypeInput?.focus()">
        @if (fieldType) {
          <mat-icon class="icon" [svgIcon]="fieldType.icon" />
        }
        <input
          #fieldTypeInput
          class="field-name-input"
          type="text"
          [class.with-icon]="!!fieldType?.icon"
          [blazeAutocomplete]="autocomplete"
          [disabled]="isSourceInlineLink"
          [readonly]="isSourceInlineLink"
          placeholder="Start typing to find a column type"
          [formControl]="fieldTypeCtrl"
          (focus)="onFieldTypeFocus(autocomplete, fieldTypeInput)"
          (blur)="onFieldTypeBlur()"
        />
      </div>
    </div>
    <div class="!hidden">
      <blaze-autocomplete
        class="mt-4 mb-4 p-2 !bg-base-white select-autocomplete"
        #autocomplete="blazeAutocomplete"
        blazeAutoActiveFirstOption
      >
        <ng-template blazeAutocompleteContent>
          @for (option of filteredFieldTypes(); track option; let i = $index) {
            @let isSelected = option.label === fieldType?.viewValue;
            <div
              class="w-full cursor-pointer py-12 flex items-center px-8 gap-8"
              [ngClass]="{
                'bg-accent-95 text-accent': isSelected,
                'bg-base-white !text-primary-darker hover:bg-base-light !hover:text-primary':
                  !isSelected,
              }"
              [blazeAutocompleteOption]="option.label"
            >
              <mat-icon [svgIcon]="option.data.icon" />
              <span>{{ option.label }}</span>
              @if (isSelected) {
                <mat-icon class="ml-auto" svgIcon="icon_check_16" />
              }
            </div>
          }
        </ng-template>
      </blaze-autocomplete>
    </div>
    @if (fieldType?.columnType === 'Rollup') {
      <div class="field-value-comment">Summarize data from linked records.</div>
    }
    <!-- #endregion -->

    <!-- #region Text format select -->
    @if (fieldType?.columnType === 'String') {
      <div class="field-properties mt-8">
        <div class="field-name">Format</div>
        <mat-radio-group
          class="current-row-toggle flex bg-base-light rounded"
          ngDefaultControl
          [value]="textFieldFormat"
          (change)="onTextFormatChanged($event)"
        >
          @for (tff of textFieldFormats; track tff) {
            <mat-radio-button class="items-center cursor-pointer flex-1" [value]="tff.value">
              {{ tff.label }}
            </mat-radio-button>
          }
        </mat-radio-group>
      </div>
    }
    <!-- #endregion -->

    <!-- #region Duration Field type select -->
    @if (
      fieldType?.columnType === 'Duration' ||
      (fieldType?.columnType === 'Formula' && fieldType?.columnDataType === 'Duration')
    ) {
      <div class="field-properties mt-8">
        <div class="field-name">Format</div>
        <blaze-select
          filterPlaceholder="Select format"
          selectClass="column-edit"
          [value]="durationFieldFormat"
          [showSelection]="true"
          [filtered]="false"
          [multiselect]="false"
          [allowNone]="false"
          [showNoneOption]="false"
          [options]="durationFieldFormats"
          [selectedTemplateRef]="durationFormatSelectedTpl"
          [optionTemplateRef]="durationFormatOptionTpl"
          (valueChange)="setDurationFormat($event)"
        />
        <ng-template #durationFormatSelectedTpl let-selectedItems>
          <ng-container>
            <div class="flex-grow pl-8">{{ durationFieldFormat || 'Default' }}</div>
          </ng-container>
        </ng-template>
        <ng-template #durationFormatOptionTpl let-option>
          <div class="flex-grow">{{ option.label }}</div>
        </ng-template>
      </div>
    }
    <!-- #endregion -->

    <!-- #region Date Time options select -->
    @if (fieldType?.columnType === 'DateTime') {
      <div class="field-properties mt-8">
        <div class="field-name">Options</div>
        <mat-radio-group
          class="current-row-toggle flex bg-base-light rounded"
          ngDefaultControl
          [value]="dateTimeFieldOption"
          (change)="onDateTimeFormatChanged($event)"
        >
          <mat-radio-button
            class="items-center cursor-pointer flex-1"
            [value]="dateTimeFieldOptions[0].value"
          >
            {{ dateTimeFieldOptions[0].label }}
          </mat-radio-button>
          <mat-radio-button
            class="items-center cursor-pointer flex-1"
            [value]="dateTimeFieldOptions[1].value"
          >
            {{ dateTimeFieldOptions[1].label }}
          </mat-radio-button>
        </mat-radio-group>
      </div>
    }
    <!-- #endregion -->

    <!-- #region Countries select -->
    @if (fieldType?.columnType === 'PhoneNumber' || fieldType?.columnType === 'ZipCode') {
      <div class="field-properties mt-8">
        <div class="field-name">Country format</div>
        <blaze-select
          [filterPlaceholder]="placeholderForCountryFormat"
          selectClass="column-edit"
          [value]="formatCulture"
          [showSelection]="true"
          [filtered]="false"
          [multiselect]="false"
          [allowNone]="allowNoneForCountryFormat"
          [options]="allCountriesLocale"
          [selectedTemplateRef]="cultureSelectedOptionTpl"
          [optionTemplateRef]="cultureOptionTpl"
          (valueChange)="onCultureFormatChanged($event)"
        />
        <ng-template #cultureSelectedOptionTpl let-selectedItems>
          <ng-container>
            @if (selectedItems?.[0]; as selected) {
              <div class="flex-grow pl-8">{{ selected.label || 1 }}</div>
            }
          </ng-container>
        </ng-template>
        <ng-template #cultureOptionTpl let-option>
          <div class="flex-grow pl-8" [ngClass]="{ 'opacity-75': !option.label }">{{
            option.label || 'Any country'
          }}</div>
        </ng-template>
      </div>
    }
    <!-- #endregion -->

    <!-- #region Number field format select -->
    @if (
      fieldType?.columnDataType === 'Number' &&
      (fieldType?.columnType === 'Number' || fieldType?.columnType === 'Formula')
    ) {
      <div class="field-properties mt-8">
        <div class="field-name">Format</div>
        <blaze-select
          filterPlaceholder="Select format"
          selectClass="column-edit"
          [value]="numberFieldFormat"
          [showSelection]="true"
          [filtered]="false"
          [multiselect]="false"
          [allowNone]="true"
          [showNoneOption]="false"
          [options]="numberFieldFormats"
          [selectedTemplateRef]="numberFormatSelectedTpl"
          [optionTemplateRef]="numberFormatOptionTpl"
          (valueChange)="setFieldFormat($event)"
        />
        <ng-template #numberFormatSelectedTpl let-selectedItems>
          <ng-container>
            <div class="flex-grow pl-8">{{ numberFieldFormat || 'Default' }}</div>
          </ng-container>
        </ng-template>
        <ng-template #numberFormatOptionTpl let-option>
          <div class="flex-grow">{{ option.label }}</div>
        </ng-template>
      </div>
    }
    <!-- #endregion -->

    <!-- #region Currency symbol select -->
    @if (fieldType?.columnDataType === 'Number' && numberFieldFormat === 'Currency') {
      <div class="field-properties mt-8">
        <div class="field-name"></div>
        <blaze-select
          filterPlaceholder="Select currency"
          selectClass="column-edit"
          [value]="currency"
          [showSelection]="true"
          [filtered]="true"
          [multiselect]="false"
          [allowNone]="false"
          [options]="currencies"
          [selectedTemplateRef]="currencySelectedTpl"
          [optionTemplateRef]="currencyOptionTpl"
          (valueChange)="setCurrency($event)"
        />
        <ng-template #currencySelectedTpl let-selectedItems>
          <div class="flex-grow pl-8">{{ currency }}</div>
        </ng-template>
        <ng-template #currencyOptionTpl let-option>
          <div class="flex-grow">{{ option.label }}</div>
        </ng-template>
      </div>
    }
    <!-- #endregion -->

    <!-- #region Rollup -->
    @if (fieldType?.columnType === 'Rollup') {
      @let rollupConfigTableIds = rollupConfigTableId$ | async;
      <div class="flex flex-col gap-8">
        <div class="field-properties mt-8">
          <div class="field-name">Linked table</div>
          <blaze-select
            filterPlaceholder="Select column"
            selectClass="column-edit"
            [value]="rollupDefinition.tableId"
            [showSelection]="true"
            [filtered]="false"
            [multiselect]="false"
            [allowNone]="false"
            [options]="rollupConfigTableIds || []"
            (valueChange)="onRollupSourceTableChange($event, rollupConfigTableIds)"
          />
        </div>
      </div>
      @if (rollupDefinition.tableId) {
        <div class="flex flex-col gap-8">
          <div class="field-properties mt-8">
            <div class="field-name">Linked column</div>
            <blaze-select
              filterPlaceholder="Select column"
              selectClass="column-edit"
              [value]="rollupDefinition.linkColumnId"
              [showSelection]="true"
              [filtered]="false"
              [multiselect]="false"
              [allowNone]="false"
              [options]="rollupConfigColumnIds || []"
              (valueChange)="onRollupSourceColumnChange($event, rollupConfigColumnIds)"
            />
          </div>
        </div>
      }
      @if (rollupDefinition.linkColumnId) {
        <div class="flex flex-col gap-8">
          <div class="field-properties mt-8">
            <div class="field-name">Aggregation method</div>
            <blaze-select
              filterPlaceholder="Select column"
              selectClass="column-edit"
              [value]="rollupDefinition.aggregateFunctionName"
              [showSelection]="true"
              [filtered]="false"
              [multiselect]="false"
              [allowNone]="false"
              [options]="aggregationFunctions"
              (valueChange)="onRollupAggregationFunctionChange($event)"
            />
          </div>
        </div>
      }

      @let aggregation = rollupDefinition.aggregateFunctionName;
      @let rollupConfiguredForColumn = rollupDefinition.tableId && rollupDefinition.linkColumnId;
      @let isAggregationConfiguredForColumn =
        aggregation && aggregation !== 'NONE' && aggregation !== 'COUNT';
      @if (rollupConfiguredForColumn && isAggregationConfiguredForColumn) {
        <div class="flex flex-col gap-8">
          <!-- #region rollup source select -->
          <div class="field-properties mt-8">
            <div class="field-name">Column to aggregate</div>
            <blaze-select
              filterPlaceholder="Select column"
              selectClass="column-edit"
              [value]="rollupDefinition.columnId"
              [showSelection]="true"
              [filtered]="false"
              [multiselect]="false"
              [allowNone]="false"
              [options]="rollupConfigTableColumns"
              (valueChange)="onRollupColumnChange($event)"
            />
          </div>
          <!-- #endregion -->
        </div>
      }
      @let isCountAggregation = rollupDefinition.aggregateFunctionName === 'COUNT';
      @let isColumnConfigured = isAggregationConfiguredForColumn && rollupDefinition.columnId;
      @if (rollupDefinition.tableId && (isCountAggregation || isColumnConfigured)) {
        <hr class="my-16" />
        <mat-slide-toggle
          class="bd-toggle pl-8"
          disableRipple
          ngDefaultControl
          [(ngModel)]="rollupFiltersEnabled"
        >
          Filter Conditions
        </mat-slide-toggle>
        <br />
        @if (rollupFiltersEnabled) {
          <blaze-complex-filter
            class="flex-col pl-8"
            [query]="rollupDefinition.query || undefined"
            (queryChange)="queryChange($event)"
          />
        }
      }
    }
    <!-- #endregion -->

    <!-- #region Number precision select -->
    @if (
      fieldType?.columnDataType === 'Number' &&
      (fieldType?.columnType === 'Number' || fieldType?.columnType === 'Formula')
    ) {
      <div class="field-properties mt-8">
        <div class="field-name">Precision</div>
        <blaze-select
          filterPlaceholder="Select precision"
          selectClass="column-edit"
          [value]="numberPrecision"
          [showSelection]="true"
          [filtered]="false"
          [multiselect]="false"
          [allowNone]="false"
          [options]="numberPrecisions"
          [selectedTemplateRef]="numberPrecisionSelectedTpl"
          [optionTemplateRef]="numberPrecisionOptionTpl"
          (valueChange)="onNumberPrecisionChanged($event)"
        />
        <ng-template #numberPrecisionSelectedTpl let-selectedItems>
          <ng-container>
            @if (selectedItems?.[0]; as selected) {
              <div class="flex-grow pl-8">{{ selected.label || 1 }}</div>
            }
          </ng-container>
        </ng-template>
        <ng-template #numberPrecisionOptionTpl let-option>
          <div class="flex-grow">{{ option.label }}</div>
        </ng-template>
      </div>
    }
    <!-- #endregion -->

    <!-- #region To where Link field is pointing -->
    @if (isLinkInfoVisible) {
      <div class="field-properties mt-8">
        <div class="field-name"></div>
        <button (click)="showLinkedColumn()" class="field-name-input link-info">
          <span>{{ sourceColumn?.name }}</span>
          <mat-icon svgIcon="icon_direction_right_24" />
          <span>{{ sourceColumn?.link?.columnName }}</span>
        </button>
      </div>
    }
    <!-- #endregion -->

    <!-- #region Link field config -->
    @if (fieldType?.columnType === 'Link' || isLinkEditView) {
      <div class="field-properties mt-8">
        <div class="field-name">Link to</div>
        @if (optionsDatasets$ | async; as optionsDatasets) {
          <div class="flex flex-col gap-4">
            <blaze-select
              [allowNone]="false"
              [blazeTooltip]="
                isLinkToDisabled
                  ? 'The linked table cannot be changed after the column is created.'
                  : undefined
              "
              [disabled]="isLinkToDisabled"
              [filtered]="false"
              [formControl]="linkedTableCtrl"
              [multiselect]="false"
              [options]="optionsDatasets"
              [optionTemplateRef]="datasetOptionTpl"
              [selectedTemplateRef]="datasetSelectedTpl"
              [showSelection]="true"
              [value]="dataset?.id"
              filterPlaceholder="Select table"
              selectClass="column-edit"
            />
            <ng-template #datasetSelectedTpl let-selectedItems>
              @if (selectedItems?.[0]?.data; as selected) {
                <span class="pl-8">{{ selected | datasetLabel }}</span>
              }
            </ng-template>
            <ng-template #datasetOptionTpl let-option>
              <span>
                {{ option.data | datasetLabel }}
                @if (option.data.projectName) {
                  ({{ option.data.projectName }})
                }
              </span>
            </ng-template>

            @if (linkedTableCtrl.getError('missingLinkedSourceTable')) {
              <div class="error">
                This column references another table that your project can't access.
                <br />Share it to view details.
              </div>
            }
          </div>
        }
      </div>
    }
    <!-- #endregion -->

    <!-- #region Tags -->
    @if (fieldType?.columnType === 'Tag' || fieldType?.columnType === 'AccessRole') {
      <div class="field-type-properties mt-20 pl-8">
        <hr class="mb-16" />
        <blaze-column-tags-editor-section
          [tags]="tags"
          [tagNamePrefix]="fieldType?.columnType === 'AccessRole' ? 'Role' : undefined"
          (tagsChange)="setTags($event)"
        />
      </div>
    }
    <!-- #endregion -->

    <!-- #region Default value -->
    @if (
      (fieldType?.columnType | canColumnTypeHaveDefaultValue) &&
      (fieldType?.columnDataType | canDataTypeHaveDefaultValue) &&
      updatedColumn &&
      showDefaultValueForTags &&
      !updatedColumn.isSecure &&
      !isFormulaForLinkEditView
    ) {
      <div class="field-properties mt-8">
        <div class="field-name">Default value</div>
        <div>
          @if (fieldType?.columnType === 'DateTime') {
            <!-- #region Date Time options select -->
            <div class="field-properties">
              <mat-radio-group
                class="current-row-toggle flex bg-base-light rounded"
                ngDefaultControl
                [value]="dateTimeDefaultValue"
                (change)="onDateTimeDefaultValueChanged($event)"
              >
                <mat-radio-button
                  class="items-center cursor-pointer flex-1"
                  [value]="dateTimeDefaultValuesOptions[0].value"
                >
                  {{ dateTimeDefaultValuesOptions[0].label }}
                </mat-radio-button>
                <mat-radio-button
                  class="items-center cursor-pointer flex-1"
                  [value]="dateTimeDefaultValuesOptions[1].value"
                >
                  {{ dateTimeDefaultValuesOptions[1].label }}
                </mat-radio-button>
              </mat-radio-group>
            </div>
          } @else if (fieldType?.columnType === 'Number') {
            <!-- #region Number options select -->
            <div class="field-properties">
              <div class="flex flex-col gap-8">
                <mat-radio-group
                  class="current-row-toggle flex bg-base-light rounded"
                  ngDefaultControl
                  [value]="numberDefaultValue"
                  (change)="onNumberDefaultValueChanged($event)"
                >
                  <mat-radio-button class="items-center cursor-pointer flex-1" [value]="null">
                    Set a Number
                  </mat-radio-button>
                  <mat-radio-button class="items-center cursor-pointer flex-1" value="NONE">
                    Set to NONE
                  </mat-radio-button>
                </mat-radio-group>
                @if (numberDefaultValue !== 'NONE') {
                  <blaze-cell-input
                    [value]="defaultValue"
                    [column]="updatedColumn"
                    [columns]="(tableConfig$ | async)?.columns || []"
                    [placeholder]="defaultValuePlaceholder"
                    (valueChanged)="setDefaultValue($event)"
                  />
                }
              </div>
            </div>
          } @else {
            <blaze-cell-input
              [isDisabled]="(fieldType?.columnType === 'Link' || isLinkEditView) && !dataset"
              [value]="defaultValue"
              [column]="updatedColumn"
              [columns]="(tableConfig$ | async)?.columns || []"
              [placeholder]="defaultValuePlaceholder"
              (valueChanged)="setDefaultValue($event)"
            />
          }
        </div>
      </div>
    }
    <!-- #endregion -->

    <!-- #region Link field available columns -->
    @if (fieldType?.columnType === 'Link' || isLinkEditView) {
      <div class="flex flex-col gap-8 mt-24">
        @if ((optionsDatasets$ | async)?.length && !dataset?.connectorName) {
          <h4 class="properties-header text-primary pl-8" [class.opacity-50]="!dataset?.name"
            >Include fields</h4
          >
        }
        <div class="linked-checks">
          @if (dataset?.columns) {
            @for (col of datasetInlineColumns; track col) {
              <blaze-checkbox
                class="mr-8"
                [class.opacity-80]="col.isDisplayColumn"
                [checked]="col.selected || col.isDisplayColumn"
                [disabled]="col.isDisplayColumn"
                (change)="setInlineColumn(col)"
              >
                <span class="flex">
                  @if (col?.icon) {
                    <mat-icon class="mr-4" [svgIcon]="col.icon" />
                  }
                  @if (col.isDisplayColumn) {
                    <span class="text-accent">* </span>
                  }
                  {{ col.originalName }}
                  @if (col.columnType === 'Link') {
                    <span class="text-primary-a-50 italic pl-4">
                      {{ col.isLink ? '(as link)' : '(as text)' }}
                    </span>
                  }
                </span>
              </blaze-checkbox>
            }
          }
        </div>
      </div>
    }
    <!-- #endregion -->

    <!-- #region Formula input -->
    @if (fieldType?.columnType === 'Formula' && !isLinkEditView) {
      <div class="field-properties mt-8">
        <div class="field-name">Formula</div>
        @if (intelliConfig$) {
          <blaze-intellisense
            class="field-name-input no-border"
            [config]="intelliConfig$ | async"
            (mousedown)="$event.stopPropagation()"
            (value)="setFormulaValue($event)"
          />
        }
      </div>
    }
    <!-- #endregion -->

    <!-- #region Signature input -->
    @if (fieldType?.columnType === 'Signature') {
      <div class="field-properties mt-8">
        <div class="field-name">Options</div>
        <mat-radio-group
          class="current-row-toggle flex bg-base-light rounded"
          ngDefaultControl
          [value]="signatureFieldOption"
          (change)="onSignatureOptionChanged($event)"
        >
          <mat-radio-button
            class="items-center cursor-pointer flex-1"
            [value]="signatureFieldOptions[0].value"
          >
            {{ signatureFieldOptions[0].label }}
          </mat-radio-button>
          <mat-radio-button
            class="items-center cursor-pointer flex-1"
            [value]="signatureFieldOptions[1].value"
          >
            {{ signatureFieldOptions[1].label }}
          </mat-radio-button>
        </mat-radio-group>
      </div>
    }
    <!-- #endregion -->

    <!-- #region File - storage type -->
    @if (fieldType?.columnType === 'File') {
      <div class="field-properties mt-8">
        <div class="field-name">Storage</div>
        <mat-radio-group
          class="current-row-toggle flex bg-base-light rounded"
          ngDefaultControl
          [value]="isSecure"
          (change)="onIsSecureChanged($event.value)"
          blazeTooltip="When stored as Secure Vault it will not be possible to export the file or reference it via URL."
        >
          <mat-radio-button class="items-center cursor-pointer flex-1" [value]="false">
            URL
          </mat-radio-button>
          <mat-radio-button class="items-center cursor-pointer flex-1" [value]="true">
            Secure Vault
          </mat-radio-button>
        </mat-radio-group>
      </div>
    }
    <!-- #endregion -->

    @if ((fieldType?.columnType | isRequiredUniqueAvailable) && !isFormulaForLinkEditView) {
      <div class="required-unique field-properties mt-16">
        <hr class="mb-16" />
        <h3 class="text-primary font-medium" [class.disabled]="isAddActionAndHasRows"
          >This Field Is</h3
        >
        <mat-slide-toggle
          name="isRequired"
          class="mt-12 bd-toggle"
          ngDefaultControl
          [disabled]="
            (isUnique && (fieldType?.columnType | shouldShowUniqueSwitch)) || isAddActionAndHasRows
          "
          [ngModel]="isRequired"
          (change)="setIsRequired($event)"
        >
          Required
        </mat-slide-toggle>
        @if (showRequiredAdditionalText) {
          <div class="text-11 text-primary bg-accent-95 flex-col p-4 ml-[38px]">
            <span class="font-medium uppercase">SOME ROWS HAVE EMPTY VALUES</span>
            <span>
              To make this field required make sure all rows have values in this column
              @if (
                (fieldType?.columnType | canColumnTypeHaveDefaultValue) &&
                (fieldType?.columnDataType | canDataTypeHaveDefaultValue) &&
                updatedColumn &&
                showDefaultValueForTags &&
                !updatedColumn.isSecure
              ) {
                or add a default value
              }
            </span>
          </div>
        }
        @if (fieldType?.columnType | shouldShowUniqueSwitch) {
          <mat-slide-toggle
            name="isUnique"
            class="bd-toggle"
            ngDefaultControl
            [disabled]="isAddActionAndHasRows"
            [ngModel]="isUnique"
            (change)="setIsUnique($event)"
          >
            Unique
          </mat-slide-toggle>
        }
        @if (isAddActionAndHasRows) {
          <div class="text-11 text-primary bg-accent-95 flex-col p-4 ml-[38px]">
            You can set this field as required or unique after all the existing rows have correct
            values.
          </div>
        }
        @if (
          fieldType?.columnType === 'File' ||
          fieldType?.columnType === 'Link' ||
          fieldType?.columnType === 'Tag' ||
          fieldType?.columnType === 'Image'
        ) {
          <mat-slide-toggle
            name="isMultipleValues"
            class="bd-toggle"
            ngDefaultControl
            [ngModel]="isMultipleValues"
            (change)="setIsMultipleValues($event)"
          >
            Allow multi-select
          </mat-slide-toggle>
        }
        @if (fieldType?.columnType === 'Tag') {
          <mat-slide-toggle
            name="allowDynamicValues"
            class="bd-toggle"
            ngDefaultControl
            [ngModel]="allowDynamicValues"
            [blazeTooltip]="
              'Enable this option to allow to add new tags dynamically, without needing to predefine them in the column settings.'
            "
            (change)="setAllowDynamicValues($event)"
          >
            Allow dynamic values
          </mat-slide-toggle>
        }
        @if (showMultiValueDataLossNotification) {
          <div class="my-8 text-11 text-warn leading-4">
            Rows that have more than one value will lose them
          </div>
        }
      </div>
    }
  </ng-container>

  <ng-container buttons>
    <button
      class="bd-btn bd-btn-primary"
      [disabled]="!isColumnDefinitionValid"
      (click)="saveCurrentField()"
    >
      {{ saveButtonLabel }}
      @if (isAddActionAndHasRows && fieldType?.columnType === 'Link' && inlineColumns.length > 0) {
        {{ inlineColumns.length + 1 }} fields
      }
    </button>
  </ng-container>
</blaze-table-popup-menu>
